import { REVIEW_TEST_IDS } from '@/components/admin/sessions/contracts';
import { type CaseDrop, ReviewCase } from '@/components/admin/sessions/review/review-case';
import { useReviewApi } from '@/components/admin/sessions/review/review-context';
import { RULE_LABEL, gridLine } from '@/lib/review-labels';

/**
 * The Rangement tab (relecture.md §5, §6): the line that says where the grid
 * comes from and whether it is frozen, then one card per case.
 *
 * Without a target anywhere (§5.1) the tab says so, and the page keeps the
 * questions, the noyau and the notes: the review is never a blank screen.
 */

export interface ReviewRangementProps {
  /** « Scénario de succès v13 » - what the grid line names, or null when nobody can say. */
  readonly protocolName: string | null;
  /** « PCQ v4 » - the protocol the session played, for the empty state. */
  readonly protocolLabel: string | null;
  /** The case an arrival lights, or null. */
  readonly flashCase: string | null;
  readonly drop: CaseDrop;
}

export function ReviewRangement({
  protocolName,
  protocolLabel,
  flashCase,
  drop,
}: ReviewRangementProps) {
  const { grid } = useReviewApi();

  if (grid === null) {
    return (
      <p className="text-gray-600 text-sm italic">
        Pas de rangement pour cette session : ni la version jouée du protocole
        {protocolLabel === null ? '' : ` (${protocolLabel})`} ni sa version de développement n’ont
        d’objectif cible. Les questions, le noyau et les notes restent ouverts.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2.5">
      <p data-testid={REVIEW_TEST_IDS.gridLine} className="text-gray-600 text-xs">
        {gridLine(grid, protocolName)}
        {' · '}
        <span className="inline-flex rounded-sm bg-gray-100 px-1.5 font-semibold text-gray-700">
          {grid.oneObjectivePerPassage ? RULE_LABEL.one : RULE_LABEL.two}
        </span>
      </p>
      {grid.objectives.map((objective, at) => (
        <ReviewCase
          key={objective.objectiveId}
          objectiveId={objective.objectiveId}
          number={at + 1}
          name={objective.name}
          flash={flashCase === objective.objectiveId}
          drop={drop}
        />
      ))}
    </div>
  );
}
